import { useState } from 'react';
import { SentraWordmark } from '@/components/brand/SentraMark';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ROUTES } from '@/constants/routes';
import { useToast } from '@/hooks/use-toast';
import { AnimatedBackground } from '@/components/common/AnimatedBackground';

export function ActivateAccount() {
  const navigate = useNavigate();
  const { uid, token } = useParams();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }
    
    try {
      await api.post('/auth/activate-account/', {
        uid,
        token,
        password
      });
      
      toast({
        title: 'Success',
        description: 'Your account has been activated successfully',
      });
      
      // Redirect to login page
      navigate(ROUTES.LOGIN);
    } catch (err) {
      setError('Invalid or expired activation link. Please contact support.');
      setIsLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen relative flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Animated Background */}
      <AnimatedBackground />
      
      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center w-full max-w-md">
        {/* Activate Account Card with integrated logo */}
        <Card className="w-full bg-white/95 backdrop-blur-sm shadow-2xl border-0 rounded-3xl overflow-hidden">
          {/* Logo section integrated into card header */}
          <CardHeader className="space-y-6 pt-8 pb-6 text-center">
            <div className="flex justify-center">
              <SentraWordmark size={44} />
            </div>
            <div className="space-y-2">
              <CardTitle className="text-3xl font-bold text-gray-800">Activate Account</CardTitle>
              <CardDescription className="text-gray-600 text-base">
                Set your password to activate your account
              </CardDescription>
            </div>
          </CardHeader>
          
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-5 px-8">
              {error && (
                <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm border border-red-200 font-medium">
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700 font-semibold text-sm">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-white border-gray-300 focus:border-sidebar-accent focus:ring-sidebar-accent rounded-xl h-12 text-gray-700"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-gray-700 font-semibold text-sm">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-white border-gray-300 focus:border-sidebar-accent focus:ring-sidebar-accent rounded-xl h-12 text-gray-700"
                  required
                />
              </div>
            </CardContent>
            
            <CardFooter className="px-8 pb-8 pt-4">
              <Button
                type="submit"
                className="w-full h-12 text-sidebar-foreground font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 text-base hover:opacity-90 bg-sidebar hover:bg-sidebar/90"
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : 'Activate Account'}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}